import React, { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { io, Socket } from 'socket.io-client';
import { useAuth } from '../hooks/useAuth';
import { useSnackbar } from 'notistack';
import { Box, Button } from '@mui/material';

interface Message {
  id: string;
  from: string;
  to: string;
  content: string;
  timestamp: Date;
  type: 'text' | 'image' | 'file';
  read: boolean;
}

interface Notification {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error';
  timestamp: Date;
  read: boolean;
  actionUrl?: string;
}

interface OnlineUser {
  id: string;
  name: string;
  role: string;
  status: 'online' | 'away' | 'busy' | 'offline';
  lastSeen?: Date;
}

interface VideoCallData {
  roomId: string;
  callerId: string;
  callerName: string;
  recipientId: string;
  type: 'video' | 'audio';
}

interface SocketContextState {
  socket: Socket | null;
  connected: boolean;
  onlineUsers: OnlineUser[];
  notifications: Notification[];
  unreadNotifications: number;
  messages: Message[];
  unreadMessages: number;
  sendMessage: (to: string, content: string, type?: 'text' | 'image' | 'file') => void;
  sendNotification: (userId: string, notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => void;
  markNotificationAsRead: (notificationId: string) => void;
  markMessageAsRead: (messageId: string) => void;
  joinRoom: (roomId: string) => void;
  leaveRoom: (roomId: string) => void;
  initiateVideoCall: (recipientId: string, type: 'video' | 'audio') => void;
  acceptVideoCall: (roomId: string) => void;
  rejectVideoCall: (roomId: string) => void;
  endVideoCall: (roomId: string) => void;
  updateUserStatus: (status: 'online' | 'away' | 'busy' | 'offline') => void;
}

const SocketContext = createContext<SocketContextState | undefined>(undefined);

interface SocketProviderProps {
  children: ReactNode;
}

export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const { enqueueSnackbar } = useSnackbar();

  const [socket, setSocket] = useState<Socket | null>(null);
  const [connected, setConnected] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState<OnlineUser[]>([]);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [incomingCall, setIncomingCall] = useState<VideoCallData | null>(null);

  // Initialize mock mode (Backend hazır olduğunda bu bölümü güncelleyin)
  useEffect(() => {
    if (!isAuthenticated || !user) {
      return;
    }

    // Mock mode - gerçek socket bağlantısı yok
    setConnected(false);

    // Mock hoş geldin bildirimi
    const mockNotifications: Notification[] = [
      {
        id: '1',
        title: 'Hoş Geldiniz',
        message: 'DrSync sistemine hoş geldiniz!',
        type: 'success',
        timestamp: new Date(),
        read: false
      }
    ];
    setNotifications(mockNotifications);

    // Mock online kullanıcılar
    const mockOnlineUsers: OnlineUser[] = [
      {
        id: '1',
        name: 'Dr. Ahmet Yılmaz',
        role: 'doctor',
        status: 'online',
        lastSeen: new Date()
      },
      {
        id: '2',
        name: 'Hemşire Ayşe',
        role: 'staff',
        status: 'busy',
        lastSeen: new Date()
      }
    ];
    setOnlineUsers(mockOnlineUsers);

  }, [isAuthenticated, user]);

  // Send message (Mock version)
  const sendMessage = useCallback(
    (to: string, content: string, type: 'text' | 'image' | 'file' = 'text') => {
      if (user) {
        const message: Message = {
          id: `${Date.now()}-${Math.random()}`,
          from: user.id,
          to,
          content,
          type,
          timestamp: new Date(),
          read: false,
        };

        // Mock mode - sadece state'e ekle
        setMessages((prev) => [...prev, message]);
        console.log('Mock: Mesaj gönderildi', message);
      }
    },
    [user]
  );

  // Send notification (Mock version)
  const sendNotification = useCallback(
    (userId: string, notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => {
      const fullNotification: Notification = {
        ...notification,
        id: `${Date.now()}-${Math.random()}`,
        timestamp: new Date(),
        read: false,
      };

      // Mock mode - sadece state'e ekle
      setNotifications((prev) => [fullNotification, ...prev]);
      console.log('Mock: Bildirim gönderildi', fullNotification);
    },
    []
  );

  // Mark notification as read (Mock version)
  const markNotificationAsRead = useCallback(
    (notificationId: string) => {
      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, read: true } : n))
      );
    },
    []
  );

  // Mark message as read (Mock version)
  const markMessageAsRead = useCallback(
    (messageId: string) => {
      setMessages((prev) =>
        prev.map((m) => (m.id === messageId ? { ...m, read: true } : m))
      );
    },
    []
  );

  // Join room (Mock version)
  const joinRoom = useCallback(
    (roomId: string) => {
      console.log('Mock: Joining room', roomId);
    },
    []
  );

  // Leave room (Mock version)
  const leaveRoom = useCallback(
    (roomId: string) => {
      console.log('Mock: Leaving room', roomId);
    },
    []
  );

  // Initiate video call (Mock version)
  const initiateVideoCall = useCallback(
    (recipientId: string, type: 'video' | 'audio') => {
      enqueueSnackbar('Video arama özelliği henüz aktif değil (Mock)', { variant: 'warning' });
    },
    [enqueueSnackbar]
  );

  // Accept video call (Mock version)
  const acceptVideoCall = useCallback(
    (roomId: string) => {
      setIncomingCall(null);
      enqueueSnackbar('Arama kabul edildi (Mock)', { variant: 'info' });
    },
    [enqueueSnackbar]
  );

  // Reject video call (Mock version)
  const rejectVideoCall = useCallback(
    (roomId: string) => {
      setIncomingCall(null);
      enqueueSnackbar('Arama reddedildi (Mock)', { variant: 'info' });
    },
    [enqueueSnackbar]
  );

  // End video call (Mock version)
  const endVideoCall = useCallback(
    (roomId: string) => {
      enqueueSnackbar('Arama sonlandırıldı (Mock)', { variant: 'info' });
    },
    [enqueueSnackbar]
  );

  // Update user status (Mock version)
  const updateUserStatus = useCallback(
    (status: 'online' | 'away' | 'busy' | 'offline') => {
      console.log('Mock: User status updated to', status);
      setOnlineUsers((prev) =>
        prev.map((u) => (u.id === user?.id ? { ...u, status } : u))
      );
    },
    [user]
  );

  // Calculate unread counts
  const unreadNotifications = notifications.filter((n) => !n.read).length;
  const unreadMessages = messages.filter((m) => !m.read && m.to === user?.id).length;

  const value: SocketContextState = {
    socket,
    connected,
    onlineUsers,
    notifications,
    unreadNotifications,
    messages,
    unreadMessages,
    sendMessage,
    sendNotification,
    markNotificationAsRead,
    markMessageAsRead,
    joinRoom,
    leaveRoom,
    initiateVideoCall,
    acceptVideoCall,
    rejectVideoCall,
    endVideoCall,
    updateUserStatus,
  };

  return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>;
};

// Custom hook to use socket context
export const useSocket = (): SocketContextState => {
  const context = useContext(SocketContext);
  if (context === undefined) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

export default SocketContext;